import { useAppDispatch, useAppSelector } from "@/store/hooks/hooks";
import { setSelectedProject } from "@/store/slices/projectSlice";
import { RootState } from "@/store/store";
import { useMsal } from "@azure/msal-react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Avatar } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { AsteriskSquare, LogOut, Menu } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Alert from "./Alert";
import ProjectDropdown from "../ProjectDropdown";
import { clearLocalStorageExcept } from "@/utils/common";

const TopBar = ({ toggleSelected }: any) => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { projects, selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );
  const { instance } = useMsal();

  const userState = useAppSelector((state: RootState) => state.user);
  const anchor = React.useRef<HTMLButtonElement | null>(null);
  const [show, setShow] = React.useState(false);
  const navigateTo = useNavigate();

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  const logout = () => {
    const user = localStorage.getItem("user");

    if (user) {
      const userData = JSON.parse(user);
      if (userData?.idProvider === "EntraID") {
        instance.logoutRedirect();
      }
    }

    clearLocalStorageExcept([
      "selectedProjectId",
      "selectedLevel",
      "selectedUsersFilter",
      "selectedStatus",
      "loginUser",
    ]);

    navigateTo("/");
  };

  const getInitials = (name: string) => {
    if (!name) return "";

    const words = name.split(" ");

    const initials = words.map((word) => word.charAt(0).toUpperCase()).join("");

    return initials.slice(0, 2);
  };

  const popupRef = useRef(null);

  // Add this useEffect to handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        show &&
        popupRef.current &&
        anchor.current &&
        !anchor.current.contains(event.target)
      ) {
        setShow(false);
      }
    };

    // Add event listener when popup is shown
    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show]);

  // Toggle function for the button
  const togglePopup = () => {
    setShow(!show);
  };

  return (
    <div
      className="flex items-center xl:px-8 sm:px-4 px-3 py-[9px] bg-[#F0F9FF] "
      id="topBar"
    >
      <div className="flex-1 flex items-center lg:gap-4 gap-2">
        {location.pathname != "/settings/projects" && (
          // <DropDownList
          //   data={projects}
          //   textField="name"
          //   dataItemKey="id"
          //   value={selectedProject}
          //   onChange={(e) => dispatch(setSelectedProject(e.value))}
          //   style={{ width: "300px" }}
          // />
          <ProjectDropdown />
        )}
      </div>
      <div className="flex sm:gap-4 gap-2 items-center">
        <Alert color="black" />

        <button
          className="p-0 border-none inline-flex items-center gap-[10px] outline-none hover:bg-transparent bg-transparent"
          onClick={onClick}
          ref={anchor}
        >
          <Avatar rounded="small" type="text">
            {userState?.user?.displayName
              ? getInitials(userState?.user?.displayName)
              : ""}
          </Avatar>
          <span className="text-sm font-normal sm:block hidden">
            {userState?.user?.displayName}
          </span>
          <span
            className="block line-clamp-1 break-words text-xs"
            style={{ color: "#918b8b" }}
          >
            {userState?.user?.roles?.join(", ")}
          </span>
        </button>

        <Popup
          anchor={anchor.current}
          show={show}
          anchorAlign={{ horizontal: "right", vertical: "bottom" }}
          popupAlign={{ horizontal: "right", vertical: "top" }}
          margin={{ horizontal: 0, vertical: 9 }}
          popupClass={"menuPopup"}
        >
          {/* Add ref to the popup content */}
          <div ref={popupRef}>
            <div className="p-2">
              <p className="block line-clamp-1 break-words text-xs">
                Signed in as
              </p>
              <span className="block line-clamp-1 break-words text-xs">
                {userState?.user?.principalName}
              </span>
              <span
                className="block line-clamp-1 break-words text-xs"
                style={{ color: "#918b8b" }}
              ></span>
            </div>
            <ul>
              <li className="border-t border-neutral-30">
                <Link
                  to="/change-password"
                  className="inline-flex items-center py-2 px-2 gap-2 text-txtPrimary hover:bg-neutral-30 w-full transition-all duration-300"
                >
                  <AsteriskSquare className="w-4 h-4" /> Change password
                </Link>
              </li>
              <li className="border-t border-neutral-30 ">
                <span
                  className="inline-flex items-center py-2 px-2 gap-2 text-txtPrimary hover:bg-neutral-30 w-full transition-all duration-300 cursor-pointer"
                  onClick={logout}
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </span>
              </li>
            </ul>
          </div>
        </Popup>
      </div>
    </div>
  );
};

export default TopBar;
